import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaWhatsapp,
  FaSearch,
  FaTimes,
  FaEye,
  FaShoppingBag,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaBoxOpen,
  FaRupeeSign,
  FaCheckCircle,
  FaClock,
  FaBan,
  FaTimesCircle,
} from "react-icons/fa";

import { useGetWhatsAppOrdersQuery,useUpdateWhatsAppOrderStatusMutation } from "../toolkitServices/whatsappApi";

/* ---------------- animation ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.22, ease: "easeOut" },
  },
});

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: Math.min(i, 10) * 0.018,
      duration: 0.22,
      ease: "easeOut",
    },
  }),
};

/* ---------------- helpers ---------------- */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-gray-800 outline-none focus:ring-1 focus:ring-violet-400";

const getSelectedBranch = () => {
  try {
    const raw =
      localStorage.getItem("selectedBranch") ||
      localStorage.getItem("branch") ||
      localStorage.getItem("currentBranch");

    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const formatAmount = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatNumber = (value, suffix = "") => {
  const num = Number(value || 0);
  return `${num.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  })}${suffix}`;
};

const getItemImage = (item) =>
  item?.imageUrl ||
  item?.images?.[0]?.url ||
  item?.images?.[0] ||
  item?.catalogItem?.imageUrl ||
  item?.labelledItem?.images?.[0]?.url ||
  "";

const statusTheme = (status = "") => {
  const s = String(status).toLowerCase();

  if (s.includes("complete") || s.includes("confirm")) {
    return {
      chip: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: <FaCheckCircle />,
      label: status || "Completed",
    };
  }

  if (s.includes("cancel")) {
    return {
      chip: "bg-rose-50 text-rose-700 border-rose-200",
      icon: <FaBan />,
      label: status || "Cancelled",
    };
  }

  if (s.includes("pending")) {
    return {
      chip: "bg-amber-50 text-amber-700 border-amber-200",
      icon: <FaClock />,
      label: status || "Pending",
    };
  }

  if (s.includes("placed")) {
    return {
      chip: "bg-blue-50 text-blue-700 border-blue-200",
      icon: <FaShoppingBag />,
      label: status || "Placed",
    };
  }

  return {
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    icon: <FaShoppingBag />,
    label: status || "-",
  };
};

const EmptyState = ({ title, subtitle, icon }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-5 text-center">
    <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
      {icon || <FaBoxOpen />}
    </div>
    <div className="text-sm font-bold text-slate-800">{title}</div>
    <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
  </div>
);

const CompactStat = ({ label, value, highlight = false }) => (
  <div
    className={`min-w-0 rounded-xl px-2 py-2 text-center ${
      highlight ? "bg-violet-50" : "bg-slate-50"
    }`}
  >
    <p
      className={`truncate text-[8px] font-bold uppercase tracking-wide ${
        highlight ? "text-violet-500" : "text-slate-400"
      }`}
    >
      {label}
    </p>
    <p
      className={`mt-1 truncate text-[11px] font-bold ${
        highlight ? "text-violet-800" : "text-slate-800"
      }`}
    >
      {value === 0 || value ? value : "-"}
    </p>
  </div>
);

const Info = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    violet: "border-violet-200 bg-violet-50 text-violet-800",
  };

  return (
    <div className={`rounded-xl border px-2.5 py-2 ${tones[tone] || tones.slate}`}>
      <div className="text-[9px] font-bold uppercase tracking-wide opacity-60">
        {label}
      </div>
      <div className="mt-1 break-words text-[11px] font-bold">
        {value === 0 || value ? value : "-"}
      </div>
    </div>
  );
};

const SectionCard = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
    <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
      {title}
    </div>
    {children}
  </div>
);

/* ---------------- modal ---------------- */
const OrderDetailModal = ({ order, onClose,onStatusChange, isStatusUpdating }) => {
  if (!order) return null;

  const items = order.items || [];

  const totalQty = items.reduce((sum, item) => sum + Number(item.qty || 1), 0);
  const totalGrossWt = items.reduce(
    (sum, item) => sum + Number(item.grossWt || 0),
    0,
  );
  const totalNetWt = items.reduce(
    (sum, item) => sum + Number(item.netWt || 0),
    0,
  );

  const calculatedTotal = items.reduce((sum, item) => {
    const qty = Number(item.qty || 1);
    const price = Number(item.price || item.mrp || 0);
    return sum + price * qty;
  }, 0);

  const currentStatus = statusTheme(order.status);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 22, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 22, scale: 0.99 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:max-h-[88vh] sm:max-w-6xl sm:rounded-2xl"
        >
          {/* Compact Modal Header */}
          <div className="shrink-0 bg-[#9481ce] text-white">
            <div className="flex justify-center pt-2 sm:hidden">
              <span className="h-1 w-10 rounded-full bg-white/45" />
            </div>

            <div className="flex items-center justify-between gap-3 px-4 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
                  <FaWhatsapp className="text-sm" />
                </span>

                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
  <h3 className="truncate text-sm font-bold sm:text-base">
    {order.orderNo || "-"}
  </h3>

  <select
    value={String(order.status || "").toLowerCase()}
    onChange={(e) => onStatusChange(order._id, e.target.value.toUpperCase())}
    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-bold text-slate-700 outline-none"
    disabled={isStatusUpdating}
  >
    <option value="placed">Placed</option>
    <option value="pending">Pending</option>
    <option value="confirmed">Confirmed</option>
    <option value="completed">Completed</option>
    <option value="cancelled">Cancelled</option>
  </select>
</div>

                  <p className="truncate text-[10px] text-violet-100">
                    WhatsApp order details
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>

          {/* Compact Top Summary */}
          <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:px-4">
            <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="truncate text-[13px] font-bold text-slate-900">
                    {order.customerName || "-"}
                  </span>

                  <span className="hidden text-slate-300 sm:inline">|</span>

                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600">
                    <FaPhoneAlt className="text-[10px] text-slate-400" />
                    {order.customerPhone || "-"}
                  </span>
                </div>

                <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[10px] font-semibold text-slate-500">
                  <FaMapMarkerAlt className="shrink-0 text-[9px] text-slate-400" />
                  <span className="truncate">
                    {order.branchName || order.branchId || "-"}
                  </span>
                </div>
              </div>

              <span
                className={`inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold sm:hidden ${currentStatus.chip}`}
              >
                {currentStatus.icon}
                {currentStatus.label}
              </span>
            </div>

            <div className="mt-2 grid grid-cols-5 gap-1.5 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
              <CompactStat label="Items" value={items.length} />
              <CompactStat label="Qty" value={totalQty} />
              <CompactStat label="Gross" value={formatNumber(totalGrossWt, "g")} />
              <CompactStat label="Net" value={formatNumber(totalNetWt, "g")} />
              <CompactStat
                label="Total"
                value={`₹${formatAmount(order.totalAmount || calculatedTotal)}`}
                highlight
              />
            </div>
          </div>

          {/* Items */}
          <div className="min-h-0 flex-1 overflow-auto bg-slate-50 p-2.5 sm:p-3">
            {!items.length ? (
              <EmptyState
                icon={<FaBoxOpen />}
                title="No item details found"
                subtitle="Order item details are not available for this order."
              />
            ) : (
              <div className="grid gap-2.5">
                {items.map((item, index) => {
                  const image = getItemImage(item);
                  const qty = Number(item.qty || 1);
                  const price = Number(item.price || item.mrp || 0);
                  const lineTotal = price * qty;

                  return (
                    <motion.div
                      key={item._id || item.catalogItemId || index}
                      custom={index}
                      variants={listVariants}
                      initial="hidden"
                      animate="visible"
                      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                    >
                      <div className="border-b border-slate-100 bg-white px-3 py-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-2">
                            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-50 text-[11px] font-bold text-violet-700">
                              {index + 1}
                            </span>

                            <div className="min-w-0">
                              <h4 className="truncate text-[13px] font-bold text-slate-900">
                                {item.name ||
                                  item.productName ||
                                  item.itemCode ||
                                  "-"}
                              </h4>
                              <p className="truncate text-[10px] font-semibold text-slate-500">
                                Code: {item.itemCode || "-"}
                              </p>
                            </div>
                          </div>

                          <div className="shrink-0 rounded-xl border border-violet-100 bg-violet-50 px-2.5 py-1.5 text-right">
                            <div className="text-[8px] font-bold uppercase tracking-wide text-violet-500">
                              Line Total
                            </div>
                            <div className="text-[11px] font-bold text-violet-800">
                              ₹{formatAmount(lineTotal)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-2.5 p-2.5 sm:p-3 lg:grid-cols-[140px_minmax(0,1fr)_160px]">
                        <div className="overflow-hidden rounded-xl border border-slate-100 bg-slate-100">
                          <div className="aspect-[4/3] w-full lg:aspect-square">
                            {image ? (
                              <img
                                src={image}
                                alt={item.name || item.itemCode || "Product"}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-[11px] font-bold text-slate-400">
                                No Image
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="min-w-0 space-y-2">
                          <SectionCard title="Product Details">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                              <Info label="Category" value={item.categoryName} />
                              <Info label="Product" value={item.productName} />
                              <Info label="Design" value={item.designName} />
                              <Info label="Purity" value={item.purityName} />
                              <Info
                                label="Gross Wt"
                                value={formatNumber(item.grossWt, " g")}
                              />
                              <Info
                                label="Net Wt"
                                value={formatNumber(item.netWt, " g")}
                              />
                              <Info label="Pcs" value={item.pcs || 0} />
                              <Info label="Qty" value={qty} />
                            </div>
                          </SectionCard>

                          <SectionCard title="Making / Stone / Diamond">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                              <Info
                                label="Making %"
                                value={item.makingPercentage || 0}
                              />
                              <Info
                                label="Making / Gm"
                                value={item.makingPerGram || 0}
                              />
                              <Info
                                label="Fixed Making"
                                value={item.fixedMaking || 0}
                              />
                              <Info
                                label="Fixed Amount"
                                value={`₹${formatAmount(item.fixedAmount)}`}
                              />
                              <Info
                                label="Stone Pcs"
                                value={item.stonePcs || 0}
                              />
                              <Info
                                label="Diamond Pcs"
                                value={item.diamondPcs || 0}
                              />
                              <Info
                                label="Stone Amt"
                                value={`₹${formatAmount(
                                  item.totalStoneAmount,
                                )}`}
                              />
                              <Info
                                label="Diamond Amt"
                                value={`₹${formatAmount(
                                  item.totalDiamondAmount,
                                )}`}
                              />
                              <Info
                                label="Hallmark"
                                value={`₹${formatAmount(
                                  item.hallmarkCharge,
                                )}`}
                              />
                              <Info
                                label="MRP"
                                value={`₹${formatAmount(
                                  item.mrp || item.price,
                                )}`}
                              />
                            </div>
                          </SectionCard>
                        </div>

                        <div className="grid grid-cols-2 gap-2 rounded-xl border border-violet-100 bg-violet-50 p-2.5 lg:grid-cols-1">
                          <Info
                            label="Price"
                            value={`₹${formatAmount(price)}`}
                            tone="violet"
                          />
                          <Info label="Qty" value={qty} tone="violet" />
                          <Info
                            label="Line Total"
                            value={`₹${formatAmount(lineTotal)}`}
                            tone="violet"
                          />
                          <Info
                            label="Order Code"
                            value={item.orderCode || "-"}
                            tone="violet"
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white p-2.5 sm:hidden">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-[#6b59c6] px-4 py-2.5 text-[12px] font-bold text-white shadow-sm"
            >
              Close Details
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ---------------- main page ---------------- */
const WhatsAppOrders = () => {
  const selectedBranch = getSelectedBranch();

  const branchId =
    selectedBranch?._id || selectedBranch?.id || selectedBranch?.branchId || "";

  const branchName =
    selectedBranch?.branchName ||
    selectedBranch?.name ||
    selectedBranch?.BranchName ||
    "";

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: ordersData, isFetching } = useGetWhatsAppOrdersQuery(
    branchId ? { branchId } : {},
  );

  const [updateWhatsAppOrderStatus, { isLoading: isStatusUpdating }] =
  useUpdateWhatsAppOrderStatusMutation();

  const orders = ordersData?.data || [];

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch =
        !q ||
        order.orderNo?.toLowerCase().includes(q) ||
        order.customerName?.toLowerCase().includes(q) ||
        order.customerPhone?.toLowerCase().includes(q) ||
        order.branchName?.toLowerCase().includes(q);

      const matchesStatus =
        !statusFilter ||
        String(order.status || "").toLowerCase() === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, search, statusFilter]);

  const placedCount = orders.filter((x) =>
    String(x.status || "").toLowerCase().includes("placed"),
  ).length;

  const pendingCount = orders.filter((x) =>
    String(x.status || "").toLowerCase().includes("pending"),
  ).length;

  const completedCount = orders.filter((x) => {
    const s = String(x.status || "").toLowerCase();
    return s.includes("complete") || s.includes("confirm");
  }).length;

  const totalAmount = orders.reduce(
    (sum, order) => sum + Number(order.totalAmount || 0),
    0,
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  const hasFilters = search || statusFilter;


  const handleStatusChange = async (orderId, newStatus) => {
  try {
    await updateWhatsAppOrderStatus({
      id: orderId,
      status: newStatus,
    }).unwrap();

    if (selectedOrder?._id === orderId) {
      setSelectedOrder((prev) =>
        prev ? { ...prev, status: newStatus } : prev
      );
    }
  } catch (error) {
    console.error("Status update failed", error);
  }
};
  return (
    <div className="relative flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl bg-white shadow-lg md:h-[calc(100vh-70px)]">
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
           order={selectedOrder}
  onClose={() => setSelectedOrder(null)}
  onStatusChange={handleStatusChange}
  isStatusUpdating={isStatusUpdating}
          />
        )}
      </AnimatePresence>

      {isFetching && !orders.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-xs text-[#6b59c6]">
            <Spinner size={26} />
            <span>Loading orders…</span>
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="shrink-0 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center justify-between gap-3 px-4 py-1.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FaWhatsapp className="shrink-0 text-base" />

              <h2 className="truncate text-[16px] font-semibold tracking-tight">
                WhatsApp Orders
              </h2>

              {isFetching && orders.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] text-white">
                  <Spinner size={9} />
                  refreshing…
                </span>
              )}
            </div>

            <p className="truncate text-[10px] text-violet-100">
              {branchName || "Orders received from WhatsApp"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold sm:inline-flex">
              Total: {orders.length}
            </span>

            <span className="hidden rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-blue-700 sm:inline-flex">
              Placed: {placedCount}
            </span>

            <span className="hidden rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700 md:inline-flex">
              Pending: {pendingCount}
            </span>

            <span className="hidden rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 lg:inline-flex">
              Completed: {completedCount}
            </span>

            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700">
              ₹{formatAmount(totalAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Compact Filters */}
      <div className="shrink-0 border-b bg-slate-50/80 px-4 py-1.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex w-[130px] flex-col gap-[1px]">
            <label className="text-[10px] font-medium text-slate-600">
              Status
            </label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${inputClass} h-7 py-1 text-[10px]`}
            >
              <option value="">All</option>
              <option value="placed">Placed</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex min-w-[170px] flex-1 flex-col gap-[1px]">
            <label className="text-[10px] font-medium text-slate-600">
              Search
            </label>

            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400" />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputClass} h-7 w-full py-1 pl-8 text-[10px]`}
                placeholder="Search order, customer, phone, branch..."
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pb-[1px]">
            <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold text-violet-700 sm:hidden">
              Total: {orders.length}
            </span>

            <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-blue-700 sm:hidden">
              Placed: {placedCount}
            </span>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                <FaTimesCircle size={9} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-2.5 sm:p-3">
        {!orders.length ? (
          <EmptyState
            icon={<FaShoppingBag />}
            title="No WhatsApp orders yet"
            subtitle="Customer orders from WhatsApp will appear here."
          />
        ) : !filteredOrders.length ? (
          <EmptyState
            icon={<FaSearch />}
            title="No matching orders"
            subtitle="Try clearing filters or searching another order number."
          />
        ) : (
          <>
            {/* Mobile cards */}
            <div className="grid gap-2.5 md:hidden">
              {filteredOrders.map((order, index) => {
                const theme = statusTheme(order.status);

                return (
                  <motion.div
                    key={order._id}
                    custom={index}
                    variants={listVariants}
                    initial="hidden"
                    animate="visible"
                    onClick={() => setSelectedOrder(order)}
                    className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                  >
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-slate-900">
                            {order.orderNo || "-"}
                          </div>
                          <div className="mt-1 truncate text-[12px] font-semibold text-slate-700">
                            {order.customerName || "-"}
                          </div>
                          <div className="truncate text-[11px] font-semibold text-slate-500">
                            {order.customerPhone || "-"}
                          </div>
                        </div>

                        <select
  value={String(order.status || "").toLowerCase()}
  onChange={(e) => handleStatusChange(order._id, e.target.value.toUpperCase())}
  className="inline-flex shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-bold text-slate-700 outline-none"
  disabled={isStatusUpdating}
>
  <option value="placed">Placed</option>
  <option value="pending">Pending</option>
  <option value="confirmed">Confirmed</option>
  <option value="completed">Completed</option>
  <option value="cancelled">Cancelled</option>
</select>
                      </div>

                      <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-2">
                        <div>
                          <div className="text-[9px] font-bold uppercase text-slate-400">
                            Items
                          </div>
                          <div className="mt-1 text-xs font-bold text-slate-800">
                            {order.items?.length || 0}
                          </div>
                        </div>

                        <div>
                          <div className="text-[9px] font-bold uppercase text-slate-400">
                            Total
                          </div>
                          <div className="mt-1 text-xs font-bold text-violet-700">
                            ₹{formatAmount(order.totalAmount)}
                          </div>
                        </div>

                        <div>
                          <div className="text-[9px] font-bold uppercase text-slate-400">
                            Source
                          </div>
                          <div className="mt-1 truncate text-xs font-bold text-slate-800">
                            {order.orderSource || "WA"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="min-w-0 truncate text-[11px] font-semibold text-slate-500">
                          {order.branchName || order.branchId || "-"}
                        </div>

                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-700"
                        >
                          <FaEye size={11} />
                          View
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-[12px]">
                  <thead className="sticky top-0 z-10 bg-[#e6e1f5] text-[#4b3d91]">
                    <tr>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Order No
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Customer
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Branch
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Items
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Total
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Status
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Source
                      </th>
                      <th className="border-b px-3 py-2 text-[10px] font-bold uppercase">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredOrders.map((order, index) => {
                      const theme = statusTheme(order.status);

                      return (
                        <motion.tr
                          key={order._id}
                          custom={index}
                          variants={listVariants}
                          initial="hidden"
                          animate="visible"
                          className="transition hover:bg-violet-50/40"
                        >
                          <td className="border-b border-slate-100 px-3 py-2.5 font-bold text-slate-800">
                            {order.orderNo || "-"}
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5">
                            <div className="font-bold text-slate-800">
                              {order.customerName || "-"}
                            </div>
                            <div className="text-[11px] font-semibold text-slate-500">
                              {order.customerPhone || "-"}
                            </div>
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5 font-semibold text-slate-600">
                            {order.branchName || order.branchId || "-"}
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5 font-bold text-slate-700">
                            {order.items?.length || 0}
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5 font-bold text-violet-700">
                            ₹{formatAmount(order.totalAmount)}
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5">
  <select
    value={String(order.status || "").toLowerCase()}
    onChange={(e) => handleStatusChange(order._id, e.target.value.toUpperCase())}
    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-700 outline-none focus:ring-1 focus:ring-violet-400"
    disabled={isStatusUpdating}
  >
    <option value="placed">Placed</option>
    <option value="pending">Pending</option>
    <option value="confirmed">Confirmed</option>
    <option value="completed">Completed</option>
    <option value="cancelled">Cancelled</option>
  </select>
</td>

                          <td className="border-b border-slate-100 px-3 py-2.5 font-semibold text-slate-600">
                            {order.orderSource || "WHATSAPP"}
                          </td>

                          <td className="border-b border-slate-100 px-3 py-2.5">
                            <button
                              type="button"
                              onClick={() => setSelectedOrder(order)}
                              className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-700 transition hover:bg-violet-100"
                            >
                              <FaEye size={11} />
                              View
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};



export default WhatsAppOrders;